<script lang="ts">
  import { AuctionStatus } from '@do/models';

  import { LayoutType } from '@shared/types';
  import { layoutType, layoutTypeMobile } from '@shared/ui/grid-toggler';
  import { ListLayoutFixed, ListLayoutMasonry, ListLayoutRelative, ListLayoutSingle } from '@shared/ui/list';
  import { MousePointer } from '@shared/ui/mouse-pointer';
  import { splitIntoChunks } from '@shared/utils';

  import { ArtAssetCard, artAssetCardModel } from '@entities/art-asset';
  import { AuctionCard, auctionCardModel } from '@entities/auction/auction-card';

  import { ARTWORKS_PER_PAGE } from '@pages/artworks'; // TODO - probably move fetch from artworks page to artworks entity
  import type { ArtAssetReadManyResponse } from '@pages/artworks/types';

  export let artworks: ArtAssetReadManyResponse['artworks'];

  $: artworksChunks = splitIntoChunks(artworks, ARTWORKS_PER_PAGE);

  const displayAuctionStatuses: AuctionStatus[] = [AuctionStatus.Active, AuctionStatus.Finalizing];
</script>

<MousePointer>
  <div class="hidden overflow-visible md:block">
    {#if $layoutType === LayoutType.Masonry}
      <ListLayoutMasonry products="{artworks}" let:breakpoints let:product>
        {#key product.id}
          {#if product.auction && displayAuctionStatuses.includes(product.auction.status)}
            <AuctionCard
              id="{product.auction.id}"
              status="{product.auction.status}"
              startDate="{product.auction.start_date}"
              duration="{product.auction.duration}"
              bestBid="{product.auction.best_bid}"
              initialPrice="{product.physical_purchase_price}"
              artAsset="{auctionCardModel.mapApiArtAsset(product)}"
              isImageBlurred="{!!product.content_flags.length}"
            />
          {:else}
            <ArtAssetCard
              {...artAssetCardModel.mapApiToData(product)}
              {breakpoints}
              isImageBlurred="{!!product.content_flags.length}"
            />
          {/if}
        {/key}
      </ListLayoutMasonry>
    {:else if $layoutType === LayoutType.Fixed}
      {@const containerClass = 'flex flex-col h-full'}
      {@const imageWrapperClass =
        'lg:h-[660px] md:h-[336px] bg-default-100 hover:bg-default-150 dark:bg-alt-dark dark:hover:bg-dark-250 lg:p-12 md:p-8 rounded'}
      <ListLayoutFixed products="{artworks}" let:breakpoints let:product>
        {#key product.id}
          {#if product.auction && displayAuctionStatuses.includes(product.auction.status)}
            <AuctionCard
              id="{product.auction.id}"
              status="{product.auction.status}"
              startDate="{product.auction.start_date}"
              duration="{product.auction.duration}"
              bestBid="{product.auction.best_bid}"
              initialPrice="{product.physical_purchase_price}"
              artAsset="{auctionCardModel.mapApiArtAsset(product)}"
              {containerClass}
              {imageWrapperClass}
              isImageBlurred="{!!product.content_flags.length}"
            />
          {:else}
            <ArtAssetCard
              {...artAssetCardModel.mapApiToData(product)}
              {breakpoints}
              {containerClass}
              {imageWrapperClass}
              imageClass="h-full object-contain hover:scale-[102%] transition-transform duration-500"
              isImageBlurred="{!!product.content_flags.length}"
              hasHover="{false}"
            />
          {/if}
        {/key}
      </ListLayoutFixed>
    {:else}
      {#each artworksChunks as artworks}
        <ListLayoutRelative products="{artworks}" let:breakpoints let:product>
          {#key product.id}
            {#if product.auction && displayAuctionStatuses.includes(product.auction.status)}
              <AuctionCard
                id="{product.auction.id}"
                status="{product.auction.status}"
                startDate="{product.auction.start_date}"
                duration="{product.auction.duration}"
                bestBid="{product.auction.best_bid}"
                initialPrice="{product.physical_purchase_price}"
                artAsset="{auctionCardModel.mapApiArtAsset(product)}"
                isImageBlurred="{!!product.content_flags.length}"
              />
            {:else}
              <ArtAssetCard
                {...artAssetCardModel.mapApiToData(product)}
                {breakpoints}
                isImageBlurred="{!!product.content_flags.length}"
              />
            {/if}
          {/key}
        </ListLayoutRelative>
      {/each}
    {/if}
  </div>
  <div class="overflow-visible md:hidden">
    {#if $layoutTypeMobile === LayoutType.Masonry}
      <ListLayoutMasonry products="{artworks}" let:breakpoints let:product>
        {#key product.id}
          {#if product.auction && displayAuctionStatuses.includes(product.auction.status)}
            <AuctionCard
              id="{product.auction.id}"
              status="{product.auction.status}"
              startDate="{product.auction.start_date}"
              duration="{product.auction.duration}"
              bestBid="{product.auction.best_bid}"
              initialPrice="{product.physical_purchase_price}"
              artAsset="{auctionCardModel.mapApiArtAsset(product)}"
              isImageBlurred="{!!product.content_flags.length}"
            />
          {:else}
            <ArtAssetCard
              {...artAssetCardModel.mapApiToData(product)}
              {breakpoints}
              isImageBlurred="{!!product.content_flags.length}"
            />
          {/if}
        {/key}
      </ListLayoutMasonry>
    {:else if $layoutType === LayoutType.Fixed}
      {@const containerClass = 'flex flex-col h-full'}
      {@const imageWrapperClass =
        'h-[434px] bg-default-100 hover:bg-default-150 dark:bg-alt-dark dark:hover:bg-dark-250 p-5 rounded'}
      <ListLayoutFixed products="{artworks}" let:breakpoints let:product>
        {#key product.id}
          {#if product.auction && displayAuctionStatuses.includes(product.auction.status)}
            <AuctionCard
              id="{product.auction.id}"
              status="{product.auction.status}"
              startDate="{product.auction.start_date}"
              duration="{product.auction.duration}"
              bestBid="{product.auction.best_bid}"
              initialPrice="{product.physical_purchase_price}"
              artAsset="{auctionCardModel.mapApiArtAsset(product)}"
              {containerClass}
              {imageWrapperClass}
            />
          {:else}
            <ArtAssetCard
              {...artAssetCardModel.mapApiToData(product)}
              {breakpoints}
              {containerClass}
              {imageWrapperClass}
              imageClass="h-full object-contain"
              isImageBlurred="{!!product.content_flags.length}"
            />
          {/if}
        {/key}
      </ListLayoutFixed>
    {:else}
      <ListLayoutSingle products="{artworks}" let:breakpoints let:product>
        {#key product.id}
          {#if product.auction && displayAuctionStatuses.includes(product.auction.status)}
            <AuctionCard
              id="{product.auction.id}"
              status="{product.auction.status}"
              startDate="{product.auction.start_date}"
              duration="{product.auction.duration}"
              bestBid="{product.auction.best_bid}"
              initialPrice="{product.physical_purchase_price}"
              artAsset="{auctionCardModel.mapApiArtAsset(product)}"
              isImageBlurred="{!!product.content_flags.length}"
            />
          {:else}
            <ArtAssetCard
              {...artAssetCardModel.mapApiToData(product)}
              {breakpoints}
              isImageBlurred="{!!product.content_flags.length}"
            />
          {/if}
        {/key}
      </ListLayoutSingle>
    {/if}
  </div>
</MousePointer>
